import React from "react";

// Hovering zooms to about this radius (cm) around the measured point.
const ZOOM_RADIUS = 1000;

// Rack rectangles of a map.js block, one per visible bin: the bin runs along the baseline and is
// Length deep in the fill direction.
const binRects = (block) => {
  const start = block.Baseline.Start;
  const end = block.Baseline.End;
  const length = Math.hypot(end.X - start.X, end.Y - start.Y) || 1;
  const ux = (end.X - start.X) / length;
  const uy = (end.Y - start.Y) / length;
  const fx = block.FillDirection.X * block.Length;
  const fy = block.FillDirection.Y * block.Length;

  return block.Bins.filter((bin) => !bin.Hidden).map((bin) => {
    const x0 = start.X + ux * bin.Start;
    const y0 = start.Y + uy * bin.Start;
    const x1 = start.X + ux * bin.End;
    const y1 = start.Y + uy * bin.End;
    const xs = [x0, x1, x0 + fx, x1 + fx];
    const ys = [y0, y1, y0 + fy, y1 + fy];
    return {
      code: bin.Id,
      x: Math.min(...xs),
      y: Math.min(...ys),
      width: Math.max(...xs) - Math.min(...xs),
      height: Math.max(...ys) - Math.min(...ys),
    };
  });
};

// Warehouse of a bin code.
const findWarehouse = (map, code) =>
  map.Warehouses.find((warehouse) =>
    (warehouse.Corridors || []).some((corridor) =>
      (corridor.Blocks || []).some((block) =>
        (block.Bins || []).some((bin) => bin.Id === code)
      )
    )
  ) || null;

class PalletCheckMap extends React.Component {
  state = { zoomed: false };

  render() {
    const { map, pallet } = this.props;
    if (!map) {
      return <div className="palletcheck-map__empty">Térkép betöltése…</div>;
    }

    // The map shows the warehouse of the measured position, or without one that of the booked place.
    const point = pallet.MeasuredPoint;
    const warehouse = point
      ? map.Warehouses.find((w) => w.Id === point.Warehouse)
      : pallet.BinCode
      ? findWarehouse(map, pallet.BinCode)
      : null;
    if (!warehouse) return null;


    const width = warehouse.Dimensions.Width;
    const height = warehouse.Dimensions.Height;
    const pad = Math.max(width, height) * 0.03;
    const viewWidth = width + 2 * pad;
    const viewHeight = height + 2 * pad;
    const unit = Math.max(width, height) / 100;

    // Zoom only around a measured point: it goes to the middle, about ZOOM_RADIUS around it fills the view.
    const scale = Math.max(
      1,
      Math.min(viewWidth, viewHeight) / (2 * ZOOM_RADIUS)
    );
    const transform =
      this.state.zoomed && point
        ? `translate(${width / 2}px, ${height / 2}px) scale(${scale}) translate(${-point.X}px, ${-point.Y}px)`
        : "none";
    const radius = (this.state.zoomed ? unit / scale : unit) * 1.2;

    const measuredCodes = [pallet.AutoBin, pallet.AutoBin2]
      .filter((bin) => bin && bin.Code)
      .map((bin) => bin.Code);

    const rect = (area, className, key) => (
      <rect
        key={key}
        className={className}
        x={area.X}
        y={area.Y}
        width={area.Width}
        height={area.Height}
      />
    );
    const elements = warehouse.Elements || {};

    return (
      <div className="palletcheck-map">
        <div className="palletcheck-map__title">{warehouse.Name}</div>
        <svg
          className={
            "palletcheck-map__svg" +
            (point ? " palletcheck-map__svg--zoomable" : "")
          }
          viewBox={`${-pad} ${-pad} ${viewWidth} ${viewHeight}`}
          onMouseEnter={() => this.setState({ zoomed: true })}
          onMouseLeave={() => this.setState({ zoomed: false })}
        >
          <g className="palletcheck-map__zoom" style={{ transform }}>
            {(elements.Bases || []).map((a, i) => rect(a, "pcm-base", "b" + i))}
            {(elements.InactiveAreas || []).map((a, i) =>
              rect(a, "pcm-inactive", "i" + i)
            )}
            {(elements.Walls || []).map((a, i) => rect(a, "pcm-wall", "w" + i))}
            {warehouse.Corridors.map((corridor) =>
              corridor.Blocks.filter((block) => block.Baseline).map(
                (block, blockIndex) =>
                  binRects(block).map((r) => (
                    <rect
                      key={corridor.Id + blockIndex + r.code}
                      className={
                        "pcm-bin" +
                        (r.code === pallet.BinCode ? " pcm-bin--booked" : "") +
                        (measuredCodes.includes(r.code)
                          ? " pcm-bin--measured"
                          : "")
                      }
                      x={r.x}
                      y={r.y}
                      width={r.width}
                      height={r.height}
                    >
                      <title>{r.code}</title>
                    </rect>
                  ))
              )
            )}
            {point && (
              <circle
                className="pcm-pulse"
                cx={point.X}
                cy={point.Y}
                r={radius}
              />
            )}
            {point && (
              <circle
                className="pcm-point pcm-point--measured"
                cx={point.X}
                cy={point.Y}
                r={radius}
              >
                <title>Mért pozíció: {measuredCodes.join(", ")}</title>
              </circle>
            )}
          </g>
        </svg>
        <div className="palletcheck-map__legend">
          <span>
            <i className="pcm-swatch pcm-swatch--booked" /> könyvelt hely
          </span>
          {point && (
            <span>
              <i className="pcm-swatch pcm-swatch--measured" /> mért hely(ek)
            </span>
          )}
          {point && (
            <span>
              <i className="pcm-dot pcm-dot--measured" /> mért pozíció
            </span>
          )}
        </div>
      </div>
    );
  }
}

export default PalletCheckMap;
